import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Calls onChange (debounced) whenever any row in the given tables changes, and when the tab
 * regains focus as a fallback in case the websocket dropped. Realtime goes browser -> Supabase
 * directly, so it doesn't cost Worker requests or TMDB/OMDB calls. RLS scopes what's delivered.
 */
export function useRealtimeRefetch(tables: string[], onChange: () => void) {
  const { user } = useAuth();
  const callbackRef = useRef(onChange);
  callbackRef.current = onChange;
  const tablesKey = tables.join(',');

  useEffect(() => {
    if (!user) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const fire = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => callbackRef.current(), 800);
    };

    let channel = supabase.channel(`refetch_${tablesKey}_${user.id}`);
    for (const table of tablesKey.split(',')) {
      channel = channel.on('postgres_changes', { event: '*', schema: 'public', table }, fire);
    }
    channel.subscribe();

    const onVisible = () => { if (document.visibilityState === 'visible') fire(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      supabase.removeChannel(channel);
    };
  }, [user, tablesKey]);
}
